import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBuilding,
  faFileAlt,
  faShieldAlt,
  faMotorcycle,
} from '@fortawesome/free-solid-svg-icons';
import { MotoCompletedScreenStyles } from './completedScreens.styles';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
  CompletedSectionTitle,
} from './CompletedV2.styles';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import { apiDownloadDocument, apiDownloadOptionalDocument } from '../../services/axios-client/axeCommons';
import { getMotoCompletedData, motoCompletedDataReset } from '../../features/purchases/purchasesActions';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import ErrorComponent from '../../common/components/ErrorComponent';
import CompletedQuoteScreenAutoAfterSale from '../../common/components/elements/CompletedQuoteScreenAutoAfterSale';
import ContractDocuments from '../../common/components/elements/ContractDocuments';
import CompletedQuoteScreenInfoMoto from '../../common/components/elements/CompletedQuoteScreenInfoMoto';
import CompletedScreenMotoDocuments from '../../common/components/elements/CompletedScreenMotoDocuments';
import CompletedScreenSemestraleButton from '../../common/components/elements/CompletedScreenSemestraleButton';
import CompletedScreenCustomerLinkButton from '../../common/components/elements/CompletedScreenCustomerLinkButton';

function MotoCompletedScreen() {
  const [residenceInfo, setResidenceInfo] = useState(null);
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const { document_types } = useSelector(
    (store) => store.afterSale,
  );
  const { loading, quote, error } = useSelector((store) => store.purchases.moto_completed);

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find(
          (commune) => commune.cadastral_code
            == quote.insurance_request.customer.residence_commune_code,
        );
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const downloadOptionalDocument = async (documentId) => {
    const response = await apiDownloadOptionalDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  const collectQuoteData = () => {
    getMotoCompletedData(quoteId);
  };

  useEffect(() => {
    collectQuoteData();

    return () => {
      motoCompletedDataReset();
    };
  }, [quoteId]);

  useEffect(() => {
    if (quote && quote.status === 7) {
      collectMunicipalities();
    }
  }, [quote]);

  if (loading) {
    return <PageLoading />;
  }

  if (loading === false && error !== null) {
    return <ErrorComponent error={error} />;
  }

  const documentsRequests = [
    ...quote.insurance_request.document_requests.filter(
      (dr) => dr.document !== null,
    ),
    ...quote.document_requests.filter((dr) => dr.document !== null),
  ];

  return (
    <MotoCompletedScreenStyles>
      {quote.payments.length === 1 && quote.status === 4 && quote.payment_frequency === 2 && (
        <CompletedScreenSemestraleButton
          onNavigate={() => navigate(`/quotes/moto/purchase/${quote.insurance_request.request_token}`)}
          buttonText="Procedi al pagamento"
          disabled={false}
          v2
        />
      )}
      {quote.status === 7 && quote.insurance_request.active_customer_id && (
        <CompletedScreenCustomerLinkButton
          onNavigate={() => {
            navigate(`/clients/${quote.insurance_request.active_customer_id}`);
          }}
          buttonText="Apri dettaglio cliente"
          disabled={false}
          v2
        />
      )}
      {/* Contract Documents Section */}
      <div className="section-container">
        <ContractDocuments
          downloadDocument={downloadDocument}
          insuranceRequest={quote.insurance_request}
          quote={quote}
          collectQuoteData={collectQuoteData}
          v2
        />
      </div>

      {/* Customer & Vehicle Information Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
          <h2>Informazioni Polizza</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          {/* Customer Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-one">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon
                    icon={quote.insurance_request.customer.gender === 'G' ? faBuilding : faUser}
                  />
                </CompletedCardIcon>
                <CompletedCardTitle>Anagrafica</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField className="full">
                  <dt>
                    {quote.insurance_request.customer.gender === 'G' ? 'Compagnia' : 'Nome e Cognome'}
                  </dt>
                  <dd>
                    {quote.insurance_request.customer.gender === 'G'
                      ? quote.insurance_request.customer.business_name
                      : `${quote.insurance_request.customer.name} ${quote.insurance_request.customer.surname}`}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Email</dt>
                  <dd>{quote.insurance_request.customer.email}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Telefono</dt>
                  <dd>{quote.insurance_request.customer.phone}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Indirizzo</dt>
                  <dd>
                    {quote.insurance_request.customer.address}
                    ,
                    {' '}
                    {quote.insurance_request.customer.house_number}
                    ,
                    {' '}
                    {quote.insurance_request.customer.postal_code}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Residenza</dt>
                  <dd>{residenceInfo !== null && residenceInfo.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Città</dt>
                  <dd>
                    {residenceInfo !== null && residenceInfo.communal_territory.name}
                  </dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Prodotto</dt>
                  <dd>{quote.product.name}</dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>

          {/* Vehicle Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-two">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faMotorcycle} />
                </CompletedCardIcon>
                <CompletedCardTitle>Dati Veicolo</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField>
                  <dt>Targa</dt>
                  <dd>{quote.insurance_request.moto.moto_plate}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Marca</dt>
                  <dd>{quote.insurance_request.moto.brand.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Modello</dt>
                  <dd>{quote.insurance_request.moto.model.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Antifurto</dt>
                  <dd>{quote.insurance_request.moto.theft_protection.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Moto Comprata</dt>
                  <dd>{quote.insurance_request.moto.moto_purchased_year}</dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
        </div>
      </div>

      {/* Quote Details & Documents Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
          <h2>Dettagli Preventivo e Documenti</h2>
        </CompletedSectionTitle>

        <div className="row">
          <CompletedQuoteScreenInfoMoto
            rc_quotation={quote}
            risk_quotations={quote.risk_quotations}
          />

          <CompletedScreenMotoDocuments
            downloadDocument={downloadDocument}
            insuranceRequest={quote.insurance_request}
            quote={quote}
            collectQuoteData={collectQuoteData}
            documentsRequests={documentsRequests}
            downloadOptionalDocument={downloadOptionalDocument}
            rc_quotation={quote}
          />
        </div>
      </div>

      {/* After Sale Section */}
      <CompletedQuoteScreenAutoAfterSale
        quote={quote}
        document_types={document_types}
        v2
      />
    </MotoCompletedScreenStyles>
  );
}

export default MotoCompletedScreen;
